import {
	IconBook2,
	IconCalendar,
	IconCalendarEvent,
	IconDownload,
	IconUsersGroup,
	IconVideo,
} from "@tabler/icons-react"
import { type FC, useCallback } from "react"

import type { LandingPageBlock, LandingPageNavigationPage, ProductListType } from "@forento/core/models/landing"
import { blockDefinition } from "@forento/core/models/landingBlock"
import LandingHtmlEmbed from "@forento/ui/components/LandingHtmlEmbed"
import { cn } from "@forento/ui/utilities/tailwind"

import { ArrowIcon } from "~/components/Icon"

import BlockEditor from "../BlockEditor"
import BlogPostsBlockPreview from "./BlogPostsBlockPreview"
import FaqBlockPreview from "./FaqBlockPreview"
import FeaturesBlockPreview from "./FeaturesBlockPreview"
import FooterBlockPreview from "./FooterBlockPreview"
import GalleryBlockPreview from "./GalleryBlockPreview"
import HeaderBlockPreview from "./HeaderBlockPreview"
import HeroBlockPreview from "./HeroBlockPreview"
import LeadFormBlockPreview from "./LeadFormBlockPreview"
import ProductHighlightBlockPreview from "./ProductHighlightBlockPreview"
import ProductsBlockPreview from "./ProductsBlockPreview"
import TestimonialsBlockPreview from "./TestimonialsBlockPreview"
import TextBlockPreview from "./TextBlockPreview"
import TwoColumnsBlockPreview from "./TwoColumnsBlockPreview"

const productListPreviewConfig: Record<ProductListType, { icon: FC<{ className?: string }>; label: string }> = {
	course: { icon: IconBook2, label: "Course" },
	video: { icon: IconVideo, label: "Video" },
	downloadable: { icon: IconDownload, label: "Downloadable" },
	session: { icon: IconCalendarEvent, label: "Session" },
	event: { icon: IconCalendar, label: "Event" },
	membership: { icon: IconUsersGroup, label: "Membership" },
}

export type NavigationPreview = {
	headerPages: LandingPageNavigationPage[]
	footerPages: LandingPageNavigationPage[]
	currentPath: string
}

type Props = {
	block: LandingPageBlock
	index: number
	onMoveUp?(): void
	onMoveDown?(): void
	/** Left out by a read-only preview, which drops the block toolbar along with them. */
	onEdit?(): void
	onDelete?(): void
	isEditing?: boolean
	onChange?(index: number, block: LandingPageBlock): void
	onCancelEdit?(): void
	navigationPreview: NavigationPreview
}

const BlockPreview: FC<Props> = ({
	block,
	index,
	onMoveUp,
	onMoveDown,
	onEdit,
	onDelete,
	isEditing = false,
	onChange,
	onCancelEdit,
	navigationPreview,
}) => {
	const handleChange = useCallback(
		(updatedBlock: LandingPageBlock) => onChange?.(index, updatedBlock),
		[index, onChange],
	)
	const isPreview = !isEditing

	return (
		<div className="group relative min-h-16 w-full rounded-none border-2 border-transparent">
			{onEdit !== undefined && onDelete !== undefined && (
				<div
					className={cn(
						"absolute top-0 right-0 left-0 z-1 backdrop-blur-sm",
						isEditing
							? "pointer-events-auto opacity-100"
							: "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
					)}
				>
					<div className="border-b border-black/5 bg-linear-to-b from-white/95 to-white/90 px-4 py-3 shadow-sm sm:px-6 sm:py-4 lg:px-8">
						<div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
							<div className="min-w-0 truncate text-xs font-bold tracking-widest text-gray-900 uppercase sm:text-sm">
								Block {index + 1}: {blockDefinition(block.type).label}
								{isEditing && " (editing)"}
							</div>
							<div className="flex shrink-0 items-center gap-1 sm:gap-2">
								{onMoveUp && (
									<button
										type="button"
										onClick={onMoveUp}
										aria-label="Move up"
										className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-gray-500 select-none"
									>
										<ArrowIcon direction="up" className="size-3.5" />
										<span className="hidden text-xs font-medium sm:inline">Move up</span>
									</button>
								)}
								{onMoveDown && (
									<button
										type="button"
										onClick={onMoveDown}
										aria-label="Move down"
										className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-gray-500 select-none"
									>
										<ArrowIcon direction="down" className="size-3.5" />
										<span className="hidden text-xs font-medium sm:inline">Move down</span>
									</button>
								)}
								{!isEditing && (
									<>
										<button
											type="button"
											onClick={onEdit}
											className="cursor-pointer rounded-md border-none bg-linear-to-br from-indigo-500 to-purple-600 px-3 py-1.5 text-xs font-semibold tracking-wide text-white uppercase shadow-md sm:px-3.5"
										>
											Edit
										</button>
										<button
											type="button"
											onClick={onDelete}
											className="cursor-pointer rounded-md border-none bg-linear-to-br from-red-500 to-red-600 px-3 py-1.5 text-xs font-semibold tracking-wide text-white uppercase shadow-md sm:px-3.5"
										>
											Delete
										</button>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			)}
			<div className={cn("w-full", isPreview && "[&_a]:cursor-default")} inert={isPreview ? true : undefined}>
				{isEditing && onCancelEdit !== undefined ? (
					<BlockEditor
						block={block}
						onChange={handleChange}
						onClose={onCancelEdit}
						navigationPreview={navigationPreview}
					/>
				) : (
					<>
						{block.type === "header" && (
							<HeaderBlockPreview
								block={block}
								navigationPages={navigationPreview.headerPages}
								currentPath={navigationPreview.currentPath}
							/>
						)}
						{block.type === "hero" && <HeroBlockPreview block={block} />}
						{block.type === "html-embed" && <LandingHtmlEmbed content={block.content} showPlaceholder />}
						{block.type === "text" && <TextBlockPreview block={block} />}
						{block.type === "two-columns" && <TwoColumnsBlockPreview block={block} />}
						{block.type === "features" && <FeaturesBlockPreview block={block} />}
						{block.type === "testimonials" && <TestimonialsBlockPreview block={block} />}
						{block.type === "faq" && <FaqBlockPreview block={block} />}
						{block.type === "gallery" && <GalleryBlockPreview block={block} />}
						{block.type === "lead-form" && <LeadFormBlockPreview block={block} />}
						{block.type === "product-list" && (
							<ProductsBlockPreview
								title={block.content.title}
								icon={productListPreviewConfig[block.content.productType].icon}
								label={productListPreviewConfig[block.content.productType].label}
							/>
						)}
						{block.type === "product-highlight" && <ProductHighlightBlockPreview block={block} />}
						{block.type === "blog-posts" && <BlogPostsBlockPreview block={block} />}
						{block.type === "footer" && (
							<FooterBlockPreview
								block={block}
								navigationPages={navigationPreview.footerPages}
								currentPath={navigationPreview.currentPath}
							/>
						)}
					</>
				)}
			</div>
		</div>
	)
}

export default BlockPreview
